import { useState, useEffect } from "react";
import dayjs from "dayjs";
import { historyService } from "services/history-service";

export const useEntityHistory = (isModalOpen, entityObject) => {
    const [chartData, setChartData] = useState(null);

    useEffect(() => {
        // Only fetch if modal is open and entity exists
        if (isModalOpen && entityObject) {
            const fetchHistory = async () => {
                try {
                    let profileId = entityObject.profile?._id || entityObject.profileId || entityObject.profile?.id || entityObject.profile_id || entityObject.profile?.profileId;
                    const postId = entityObject.postId || entityObject.originalPostId || entityObject.videoId || entityObject.id;
                    const platform = entityObject.platform || entityObject.profile?.platform;

                    // Fallback: If profileId is missing and it's Facebook with a composite ID
                    if (!profileId && platform === 'Facebook' && postId && typeof postId === 'string' && postId.includes('_')) {
                        profileId = postId.split('_')[0];
                    }

                    if (profileId && postId) {
                        const history = await historyService.fetchPostHistory(profileId, postId, platform);

                        if (history && history.length > 0) {
                            // Process History Data
                            const labels = history.map(h => dayjs(h.snapshotDate).format("D MMM HH:mm"));

                            // Auto-detect best metric to show
                            // Priority: Media Views -> Video Views -> Impressions -> Engagement -> Likes
                            const sampleMetrics = history[history.length - 1].metrics || {};

                            let metricKey = "views";

                            const priorityList = [
                                "views",
                                "post_media_view",
                                "media_views",
                                "post_video_views",
                                "estimated_post_views",
                                "post_impressions",
                                "impressions",
                                "estimated_post_reach",
                                "likes"
                            ];

                            for (const key of priorityList) {
                                // We want the first metric that actually has data (> 0)
                                if (sampleMetrics[key] !== undefined && sampleMetrics[key] > 0) {
                                    metricKey = key;
                                    break;
                                }
                            }

                            // Fallback: If absolutely nothing was > 0, just use the first available defined key
                            if (sampleMetrics[metricKey] === undefined || sampleMetrics[metricKey] === 0) {
                                for (const key of priorityList) {
                                    if (sampleMetrics[key] !== undefined) {
                                        metricKey = key;
                                        break;
                                    }
                                }
                            }

                            const data = history.map(h => (h.metrics && h.metrics[metricKey]) || 0);

                            const labelMap = {
                                "views": "Views",
                                "estimated_post_views": "Est. Views",
                                "estimated_post_reach": "Est. Reach",
                                "post_impressions": "Impressions",
                                "post_media_view": "Media Views",
                                "post_video_views": "Video Views",
                                "likes": "Likes"
                            };

                            const chartLabel = labelMap[metricKey] || metricKey.replace(/_/g, ' ');

                            setChartData({
                                labels,
                                datasets: { label: chartLabel, data }
                            });
                        } else {
                            setChartData(null);
                        }
                    }
                } catch (err) {
                    console.error("Failed to load post history", err);
                    setChartData(null);
                }
            };

            fetchHistory();
        } else {
            setChartData(null);
        }
    }, [isModalOpen, entityObject]);

    return chartData;
};
